// @ts-nocheck
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  HostListener,
  ChangeDetectorRef,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LoggerService } from '@way/theme-host/services/logger.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { PackageService } from '@way/theme-host/services/package.service';
import { Package } from '@way/theme-host/shared/types';
import {
  ChampsPageHeaderComponent,
  NavigationItem,
  Logo,
  CTAButton,
} from '../shared/page-header/page-header.component';
import { ChampsPageFooterComponent } from '../shared/footer/footer.component';
import { LocationService } from '@way/theme-host/services/location.service';
import { environment } from '@way/theme-host/env/environment';
import { getReferrerUrl } from '@way/theme-host/shared/utils/website-url.util';
import { NotificationService } from '../../../../app/services/notification.service'; // NotificationService;
import { resolveFooterQuickLinks } from '@way/theme-host/shared/utils/footer-quick-links.util';

export interface WebsiteElement {
  id: string;
  type:
    | 'header'
    | 'hero'
    | 'section'
    | 'testimonials'
    | 'services'
    | 'booking'
    | 'contact'
    | 'footer'
    | 'fleets'
    | 'packages'
    | 'coupons'
    | 'express-details';
  content: any;
  selected?: boolean;
}

interface FleetFormData {
  company_name: string;
  contact_name: string;
  phone: string;
  email: string;
  wash_type: string;
  vehicle_count: number | null;
  location: string;
  notes: string;
}

@Component({
  selector: 'app-champs-fleet-management-preview',
  standalone: true,
  providers: [NotificationService],
  imports: [CommonModule, FormsModule, ChampsPageHeaderComponent, ChampsPageFooterComponent],
  templateUrl: './fleet-management-preview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./fleet-management-preview.component.scss'],
})
export class ChampsFleetManagementPreviewComponent implements OnInit, OnChanges, OnDestroy {
  @Input() pageData: any = null;
  @Input() sectionVisibility: Record<string, boolean> = {};
  @Input() pages: any[] = [];
  @Input() currentPageType = '';
  @Input() elements: WebsiteElement[] = [];
  @Input() deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  @Input() primaryColor: string | null = null; // Primary color from theme
  @Input() headingColor: string | null = null; // Heading color from branding
  @Input() paragraphColor: string | null = null; // Paragraph/description color from branding
  @Input() headingFont: string | null = null; // Heading font from branding
  @Input() bodyFont: string | null = null; // Body font from branding
  @Output() elementSelect = new EventEmitter<WebsiteElement>();
  @Output() navigationClick = new EventEmitter<string>();

  packages: Package[] = [];
  loading = false;
  submitting = false;

  // Form data
  formData: FleetFormData = {
    company_name: '',
    contact_name: '',
    phone: '',
    email: '',
    wash_type: '',
    vehicle_count: null,
    location: '',
    notes: '',
  };

  // Locations
  locations: any[] = [];
  isLoadingLocations = false;
  locationsError = '';

  // Dropdown states
  showWashTypeDropdown = false;
  showLocationDropdown = false;

  // Page data from JSON
  headerData: any = null;
  heroSectionData: any = null;
  pageTitleData: any = null;
  formSectionData: any = null;
  footerData: any = null;

  // Loading and animation state
  isPageLoaded = false;
  private intersectionObserver?: IntersectionObserver;
  private animatedElements = new Set<Element>();

  constructor(
    private logger: LoggerService,
    private packageService: PackageService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private locationService: LocationService,
    private messageService: NotificationService
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.dropdown-wrapper')) {
      this.showWashTypeDropdown = false;
      this.showLocationDropdown = false;
    }
  }

  toggleWashTypeDropdown(event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.showWashTypeDropdown = !this.showWashTypeDropdown;
    if (this.showWashTypeDropdown) {
      this.showLocationDropdown = false;
    }
  }

  /**
   * Toggle the visibility of the location dropdown, preventing event propagation when an event is provided and respecting loading/no-location guards.
   * @example
   * oggleLocationDropdown(new Event('click'))
   * undefined
   * @param {{Event|undefined}} {{event}} - Optional event to stop propagation and prevent default behavior before toggling.
   * @returns {{void}} No return value.
   **/
  toggleLocationDropdown(event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (this.isLoadingLocations || this.locations.length === 0) {
      return; // Don't open if disabled
    }
    this.showLocationDropdown = !this.showLocationDropdown;
    if (this.showLocationDropdown) {
      this.showWashTypeDropdown = false;
    }
  }

  selectWashType(packageName: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.formData.wash_type = packageName;
    this.showWashTypeDropdown = false;
  }

  selectLocation(locationId: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.formData.location = locationId;
    this.showLocationDropdown = false;
  }

  getSelectedWashTypeName(): string {
    return this.formData.wash_type || 'Select wash type';
  }

  getSelectedLocationName(): string {
    if (!this.formData.location) {
      return 'Select location';
    }
    const selectedLocation = this.locations.find(loc => loc.id === this.formData.location);
    return selectedLocation ? selectedLocation.name : 'Select location';
  }

  /**
   * Initialize component: load page content, packages, and locations, set initial loading state, trigger change detection, and initialize scroll animations after short delays.
   * @example
   * gOnInit()
   * // returns void
   * @returns {void} No value is returned.
   **/
  ngOnInit(): void {
    this.loadPageContent();
    this.loadPackages();
    this.loadLocations();

    // Initialize page loading state
    this.isPageLoaded = false;

    // Simulate page load animation
    setTimeout(() => {
      this.isPageLoaded = true;
      this.cdr.detectChanges();
      // Initialize scroll animations after page loads
      setTimeout(() => this.initScrollAnimations(), 100);
    }, 300);
  }

  ngOnDestroy(): void {
    // Clean up intersection observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    this.animatedElements.clear();
  }

  /**
   * Initialize scroll animations using Intersection Observer
   */
  private initScrollAnimations(): void {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return; // Fallback for browsers without IntersectionObserver
    }

    // Use requestAnimationFrame for smoother performance
    requestAnimationFrame(() => {
      // Create intersection observer for scroll animations with optimized settings
      this.intersectionObserver = new IntersectionObserver(
        entries => {
          // Use requestAnimationFrame to batch DOM updates
          requestAnimationFrame(() => {
            entries.forEach(entry => {
              if (entry.isIntersecting && !this.animatedElements.has(entry.target)) {
                entry.target.classList.add('animate-in');
                this.animatedElements.add(entry.target);
                // Unobserve after animation to improve performance
                this.intersectionObserver?.unobserve(entry.target);
              }
            });
          });
        },
        {
          threshold: [0.1, 0.2], // Multiple thresholds for smoother detection
          rootMargin: '0px 0px -80px 0px', // Reduced margin for better scroll performance
        }
      );

      // Observe all animatable elements with delay to avoid blocking initial render
      setTimeout(() => {
        const elements = document.querySelectorAll('.fleet-management-preview .website-element');
        elements.forEach(el => {
          // Check if element is already in viewport
          const rect = el.getBoundingClientRect();
          const isInViewport = rect.top < window.innerHeight + 100 && rect.bottom > -100;

          el.classList.add('scroll-animate');

          // If element is already visible, animate it immediately
          if (isInViewport) {
            requestAnimationFrame(() => {
              el.classList.add('animate-in');
              this.animatedElements.add(el);
            });
          } else {
            // Only observe if not already in viewport
            this.intersectionObserver?.observe(el);
          }
        });
      }, 300);
    });
  }

  /**
   * Handles Angular OnChanges for the component: loads page content when pageData updates, updates header navigation when pages or currentPageType change, and refreshes page title and form section data when elements change (to apply background updates).
   * @example
   * gOnChanges({ pageData: { firstChange: false }, pages: [], elements: { firstChange: false }, currentPageType: 'default' })
   * undefined
   * @param {{SimpleChanges}} {{changes}} - Angular SimpleChanges object containing changed input properties.
   * @returns {{void}} No return value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageData'] && !changes['pageData'].firstChange) {
      this.loadPageContent();
    }
    // Update navigation when pages or currentPageType changes
    if ((changes['pages'] || changes['currentPageType']) && this.headerData) {
      this.updateHeaderNavigation();
    }
    // Reload page content when elements change (for background color updates)
    if (changes['elements'] && !changes['elements'].firstChange) {
      // Force reload of pageTitleData and formSectionData to get updated background color
      const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
      const sections = page?.sections || {};
      this.pageTitleData = sections['page-title'] || {};
      this.formSectionData = sections.form_section || {};
    }
  }

  /**
   * Initialize and populate component section properties from the current pageData for the Fleet Management preview.
   * @example
   * loadPageContent()
   * // undefined
   * @returns {void} No return value; extracts sections from this.pageData (supports editor and preview formats), assigns headerData, heroSectionData, pageTitleData, formSectionData, footerData, and calls updateHeaderNavigation().
   */
  private loadPageContent(): void {
    if (!this.pageData) return;

    this.logger.debug('?? Loading Fleet Management page content:', this.pageData);

    // Extract sections from page data - handle both editor and preview formats
    // Editor format: pageData.page.sections
    // Preview format: pageData.json_data.page.sections
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    const sections = page?.sections || this.pageData?.sections || {};

    this.headerData = sections.header || {};
    this.heroSectionData = sections.hero_section || {};
    this.pageTitleData = sections['page-title'] || {};
    this.formSectionData = sections.form_section || {};
    this.footerData = sections.footer || {};

    this.logger.debug('Fleet page sections loaded:', {
      header: this.headerData,
      hero: this.heroSectionData,
      pageTitle: this.pageTitleData,
      form: this.formSectionData,
      footer: this.footerData,
    });

    // Update header navigation to match current pages
    this.updateHeaderNavigation();
  }

  /**
   * Update the component's headerData.navigation from pages and ensure a default CTA button if missing.
   * @example
   * updateHeaderNavigation()
   * undefined
   * @param {{any}} {{this}} - Component instance containing headerData, pages, and currentPageType used to build the navigation and CTA button.
   * @returns {{void}} Updates headerData.navigation and headerData.ctaButton in-place; returns nothing.
   **/
  private updateHeaderNavigation(): void {
    if (!this.headerData) this.headerData = {};

    // Generate navigation from pages if pages are provided
    // Filter out inactive pages (visible: false means active: false)
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      this.headerData.navigation = activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        active: page.pageType === this.currentPageType,
      }));
      this.logger.debug(
        'Updated header navigation for Fleet Management:',
        this.headerData.navigation
      );
    }

    // Ensure CTA button exists
    if (!this.headerData.ctaButton && !this.headerData.button) {
      this.headerData.ctaButton = {
        text: '',
        link: '#booking',
        backgroundColor: '#1B5BFF',
        textColor: '#FFFFFF',
      };
    }
  }

  /**
   * Loads car wash packages via the packageService, updates this.packages on success, and manages the this.loading flag.
   * @example
   * loadPackages()
   * // void — updates component state asynchronously
   * @returns {void} Updates component state asynchronously; no direct return value.
   */
  private loadPackages(): void {
    this.loading = true;

    this.packageService.getCarWashPackages().subscribe({
      next: response => {
        this.logger.debug('? Packages loaded for fleet form:', response);
        if (response.status && response.data) {
          this.packages = response.data;
        }
        this.loading = false;
      },
      error: err => {
        this.logger.error('? Error loading packages:', err);
        this.loading = false;
      },
    });
  }

  /**
   * Check if we're in website preview mode (URL contains /preview?websiteId=)
   */
  private isWebsitePreview(): boolean {
    return (
      window.location.href.includes('/preview?websiteId=') ||
      window.location.href.includes('/preview?websiteld=')
    );
  }

  private isLocalhostOrPreview(): boolean {
    const hostname = window.location.hostname;
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '0.0.0.0' ||
      hostname.startsWith('192.168.') ||
      hostname.startsWith('10.') ||
      hostname.startsWith('172.') ||
      window.location.href.includes('preview') ||
      window.location.href.includes('localhost')
    );
  }

  /**
   * Check if we're in admin/preview mode (has token in localStorage)
   */
  private isAdminMode(): boolean {
    try {
      const userData = localStorage.getItem('userData');
      const token = localStorage.getItem('token');
      return !!(userData || token);
    } catch {
      return false;
    }
  }

  /**
   * Load locations into the component's state: fetches via LocationService in admin mode, filters and maps valid locations, auto-selects the first location when appropriate, and sets loading/error state when not authorized or on failure.
   * @example
   * // Called on the component instance
   * this.oadLocations();
   * // returns void
   * @returns {void} Updates component state (locations, formData.location, isLoadingLocations, locationsError) and does not return a value.
   **/
  loadLocations(): void {
    if (this.locations.length > 0) {
      return; // Already loaded
    }

    if (this.isLoadingLocations) {
      return; // Already loading
    }

    this.isLoadingLocations = true;
    this.locationsError = '';

    // Check if in admin mode (has token) - use LocationService which handles auth
    if (this.isAdminMode()) {
      this.logger.debug('📍 Loading locations using LocationService (admin mode)');
      this.locationService.getLocations().subscribe({
        next: response => {
          this.isLoadingLocations = false;
          if (response && response.status && response.data && Array.isArray(response.data)) {
            this.locations = response.data
              .filter((location: any) => {
                return (
                  location &&
                  typeof location === 'object' &&
                  (location.status === true || location.status === 'active')
                );
              })
              .map((location: any) => {
                return {
                  id: location?.id || '',
                  name: location?.branch || location?.name || 'Unknown Location',
                  address:
                    `${location?.street_address || ''}, ${location?.city || ''}, ${location?.state || ''} ${location?.zip_code || ''}`.trim(),
                  fullData: location,
                };
              });

            if (this.locations.length === 0) {
              this.locationsError = 'No locations found.';
            } else {
              // Auto-select first location if available
              if (!this.formData.location && this.locations[0]?.id) {
                this.formData.location = this.locations[0].id;
              }
            }
          } else {
            this.logger.warn('Invalid location response format or no locations:', response);
            this.locations = [];
            this.locationsError = 'No locations found.';
          }
        },
        error: error => {
          this.logger.error('Error loading locations via LocationService:', error);
          this.isLoadingLocations = false;
          this.locations = [];
          this.locationsError = 'No locations found. Please try again later.';
        },
      });
      return;
    }

    // Not in admin mode - for preview components, we require authentication
    this.logger.debug('📍 Not in admin mode - locations require authentication');
    this.isLoadingLocations = false;
    this.locations = [];
    this.locationsError = 'Please log in to load locations.';
  }

  /**
   * Submits the fleet form: validates required fields, blocks submission in website preview, handles localhost/preview demo flow, prepares headers (including optional Authorization from localStorage), constructs request body and posts to the versioned API endpoint (baseUrl + apiVersion + '/chat/fleet'), and handles success/error responses with user alerts and form resets.
   * @example
   * nSubmit()
   * void
   * @param {void} none - No parameters.
   * @returns {void} No return value; performs side-effects (alerts, console logs, HTTP request, and form state updates).
   **/
  onSubmit(): void {
    // Check if we're in website preview mode first
    if (this.isWebsitePreview()) {
      this.messageService.add({
        severity: 'info',
        summary: 'Preview Mode',
        detail: 'Form submission is only available on the published website.',
      });
      return;
    }

    // Validate required fields
    if (
      !this.formData.company_name ||
      !this.formData.contact_name ||
      !this.formData.phone ||
      !this.formData.email ||
      !this.formData.wash_type ||
      !this.formData.vehicle_count ||
      !this.formData.location
    ) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields.',
      });
      return;
    }

    // Find the selected package to get its ID
    const selectedPackage = this.packages.find(pkg => pkg.package_name === this.formData.wash_type);
    if (!selectedPackage || !selectedPackage.id) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please select a valid wash type.',
      });
      return;
    }

    // Check if we're in localhost or preview environment
    if (this.isLocalhostOrPreview()) {
      this.messageService.add({
        severity: 'success',
        summary: 'Demo Mode',
        detail: 'Form submitted successfully (demo mode).',
      });
      // Reset form
      this.formData = {
        company_name: '',
        contact_name: '',
        phone: '',
        email: '',
        wash_type: '',
        vehicle_count: null,
        location: '',
        notes: '',
      };
      return;
    }

    this.submitting = true;

    // Get referrer URL dynamically (like express washes)
    const referrer = getReferrerUrl();

    // Get token from localStorage if available (for admin/preview mode)
    let authToken: string | null = null;
    try {
      const userData = localStorage.getItem('userData');
      if (userData) {
        const parsedData = JSON.parse(userData);
        authToken = parsedData?.token || null;
      }
    } catch (error) {
      this.logger.error('Error parsing userData:', error);
    }

    // Prepare request headers
    let headers = new HttpHeaders({
      Referer: referrer,
      'Content-Type': 'application/json',
    });

    // Add Authorization header if token is available
    if (authToken) {
      headers = headers.set('Authorization', `Bearer ${authToken}`);
    }

    // Prepare request body matching expected API structure
    const requestBody = {
      name: this.formData.company_name,
      customer_name: this.formData.contact_name,
      contact: this.formData.phone,
      email: this.formData.email,
      package_id: selectedPackage.id,
      location_id: this.formData.location,
      vehicle_count: this.formData.vehicle_count,
      special_notes: this.formData.notes || '',
    };

    // Construct API URL from environment baseUrl
    const apiUrl = `${environment.apiUrl}/chat/fleet`;

    // Make API call
    this.http.post<any>(apiUrl, requestBody, { headers }).subscribe({
      next: response => {
        this.submitting = false;
        this.logger.debug('Fleet created successfully:', response);

        if (response.status && response.status_code === 200) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Fleet created successfully!',
          });
          this.formData = {
            company_name: '',
            contact_name: '',
            phone: '',
            email: '',
            wash_type: '',
            vehicle_count: null,
            location: '',
            notes: '',
          };
        } else {
          if (response.message && response.message.includes('API key not found')) {
            this.messageService.add({
              severity: 'error',
              summary: 'Configuration Error',
              detail: 'This domain is not configured for API access. Please contact support.',
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: response.message || 'Failed to create fleet. Please try again.',
            });
          }
        }
      },
      error: error => {
        this.submitting = false;

        const errorResponse = error.error;
        if (
          errorResponse &&
          errorResponse.message &&
          (errorResponse.message.includes('API key not found') ||
            errorResponse.message.includes('API key not found for domain'))
        ) {
          this.messageService.add({
            severity: 'error',
            summary: 'Configuration Error',
            detail: 'This domain is not configured for API access. Please contact support.',
          });
        } else {
          const errorMessage =
            errorResponse?.message || error.message || 'Failed to create fleet. Please try again.';
          this.messageService.add({ severity: 'error', summary: 'Error', detail: errorMessage });
        }
      },
    });
  }

  /**
   * Check if a section should be visible based on section visibility settings
   */
  isSectionVisible(sectionKey: string): boolean {
    if (!this.sectionVisibility || Object.keys(this.sectionVisibility).length === 0) {
      return true;
    }
    return this.sectionVisibility[sectionKey] !== false;
  }

  // Header data helpers for shared component
  /**
   * Return the header logo object, preferring header element content, then pageData branding, and falling back to a default.
   * @example
   * etHeaderLogo()
   * { image: '', text: 'Logo', link: '/', alt: 'Logo' }
   * @returns {{Logo}} Logo object with shape { image: string, text: string, link: string, alt: string }.
   **/
  getHeaderLogo(): Logo {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    // Priority 1: Check header element content (most up-to-date)
    if (headerContent?.logo) {
      // If logo has image/url, return it
      if (headerContent.logo.image || headerContent.logo.url) {
        return {
          image: headerContent.logo.image || headerContent.logo.url || '',
          text: headerContent.logo.text || 'Logo',
          link: headerContent.logo.link || '/',
          alt: headerContent.logo.alt || 'Logo',
        };
      }
      // If logo has text but no image, return text logo
      if (headerContent.logo.text) {
        return {
          image: '',
          text: headerContent.logo.text,
          link: headerContent.logo.link || '/',
          alt: headerContent.logo.alt || 'Logo',
        };
      }
    }
    // Priority 2: Check branding data from pageData
    if (this.pageData?.json_data?.website?.branding?.logo) {
      const brandingLogo = this.pageData.json_data.website.branding.logo;
      if (brandingLogo.url || brandingLogo.image) {
        return {
          image: brandingLogo.url || brandingLogo.image || '',
          text: brandingLogo.text || 'Logo',
          link: brandingLogo.link || '/',
          alt: brandingLogo.alt || 'Logo',
        };
      }
      if (brandingLogo.text) {
        return {
          image: '',
          text: brandingLogo.text,
          link: brandingLogo.link || '/',
          alt: brandingLogo.alt || 'Logo',
        };
      }
    }
    return { text: 'Logo', link: '/', image: '', alt: 'Logo' };
  }

  /**
   * Builds header navigation items from the component header content or fallback pages, normalizing links and ensuring pageType is set.
   * @example
   * this.etHeaderNavigation()
   * [{ label: 'Home', link: '#home', pageType: 'home', active: true }, { label: 'Express Washes', link: '#express-wash-packages', pageType: 'express-wash-packages', active: false }]
   * @returns {NavigationItem[]} Array of navigation items with label, link, pageType, and optional active flag.
   **/
  getHeaderNavigation(): NavigationItem[] {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.navigation && Array.isArray(headerContent.navigation)) {
      // Ensure navigation items have pageType set
      return headerContent.navigation.map((nav: any) => {
        // If pageType is not set, try to extract it from link or find matching page
        if (!nav.pageType) {
          const matchingPage = this.pages?.find(
            (p: any) =>
              p.name === nav.label ||
              (p as any).pageType === nav.link?.replace(/^#+|\/+$/g, '').replace(/^\/+/, '')
          );
          if (matchingPage) {
            nav.pageType = (matchingPage as any).pageType;
          } else {
            // Extract from link
            const normalizedLink = (nav.link || '')
              .replace(/^#+|\/+$/g, '')
              .replace(/^\/+/, '')
              .toLowerCase();
            if (
              normalizedLink === 'expresswashes' ||
              normalizedLink === 'express-washes' ||
              normalizedLink.includes('express-wash')
            ) {
              nav.pageType = 'express-wash-packages';
            } else if (
              normalizedLink === 'expressdetails' ||
              normalizedLink === 'express-details' ||
              normalizedLink.includes('express-detail') ||
              normalizedLink.includes('express-auto')
            ) {
              nav.pageType = 'express-auto-details';
            } else if (
              normalizedLink === 'fleets' ||
              normalizedLink === 'fleet' ||
              normalizedLink.includes('fleet')
            ) {
              nav.pageType = 'fleet-management';
            } else if (
              normalizedLink === 'coupons' ||
              normalizedLink === 'coupon' ||
              normalizedLink.includes('coupon')
            ) {
              nav.pageType = 'coupons';
            } else if (
              normalizedLink === '' ||
              normalizedLink === 'home' ||
              normalizedLink === '/'
            ) {
              nav.pageType = 'home';
            } else {
              nav.pageType = normalizedLink || 'home';
            }
          }
        }
        return nav;
      });
    }
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      return activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        pageType: (page as any).pageType || 'home', // Ensure pageType is set
        active: page.pageType === this.currentPageType,
      }));
    }
    return [];
  }

  /**
   * Return header CTA button configuration from header content or sensible defaults.
   * @example
   * etHeaderCTAButton()
   * { text: 'Book Wash', link: '#', backgroundColor: '#1B5BFF', textColor: '#FFFFFF' }
   * @param {{void}} {{none}} - No parameters.
   * @returns {{CTAButton|null}} Return a CTAButton object with text, link, backgroundColor, and textColor, or null.
   **/
  getHeaderCTAButton(): CTAButton | null {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    const primaryColor = this.getPrimaryColor();
    if (headerContent?.ctaButton) {
      return {
        text: headerContent.ctaButton.text || headerContent.ctaButton.label || 'Book Wash',
        link: headerContent.ctaButton.link || '#',
        // Use button's backgroundColor if provided, otherwise fall back to primaryColor
        backgroundColor: headerContent.ctaButton.backgroundColor || primaryColor || '#1B5BFF',
        textColor: headerContent.ctaButton.textColor || '#FFFFFF',
      };
    }
    return {
      text: 'Book Wash',
      link: '#',
      backgroundColor: primaryColor,
      textColor: '#FFFFFF',
    };
  }

  getHeaderBackgroundColor(): string | null {
    // Header is inside hero section (like home page), so use transparent background
    // to inherit the hero section's background color
    return 'transparent';
  }

  /**
   * Get primary color from theme or use default
   */
  getPrimaryColor(): string {
    return this.primaryColor || '#1B5BFF';
  }

  /**
   * Get heading color from branding or use default
   */
  getHeadingColor(): string {
    return this.headingColor || '#15172E';
  }

  /**
   * Get paragraph color from branding or use default
   */
  getParagraphColor(): string {
    return this.paragraphColor || '#6B7280';
  }

  /**
   * Get heading font from branding or use default
   */
  getHeadingFont(): string {
    return this.headingFont || 'Manrope';
  }

  /**
   * Get body font from branding or use default
   */
  getBodyFont(): string {
    return this.bodyFont || 'Inter';
  }

  /**
   * Get hero title text
   */
  getPageTitle(): string {
    if (this.pageTitleData?.title) {
      if (typeof this.pageTitleData.title === 'string') {
        return this.pageTitleData.title;
      } else if (this.pageTitleData.title?.label) {
        return this.pageTitleData.title.label;
      }
    }
    return 'Fleet Management';
  }

  /**
   * Get hero subtitle/description text
   */
  getPageSubtitle(): string {
    if (this.pageTitleData?.description) {
      return this.pageTitleData.description;
    } else if (this.pageTitleData?.subtitle) {
      return this.pageTitleData.subtitle;
    }
    return 'Keep your business vehicles spotless and road-ready with our tailored fleet wash plans. Enjoy convenient scheduling, consistent quality, and cost savings designed for companies of any size.';
  }

  /**
   * Get title styles for ngStyle binding
   */
  getTitleStyles(): any {
    // Check page-level title first (highest priority)
    // Handle both editor and preview formats
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    if (page?.title && typeof page.title === 'object' && page.title.style?.text_style) {
      const titleStyleObj = page.title.style.text_style;
      const styles: any = {};
      if (titleStyleObj.fontWeight) styles['font-weight'] = titleStyleObj.fontWeight;
      if (titleStyleObj.fontStyle) styles['font-style'] = titleStyleObj.fontStyle;
      if (titleStyleObj.textDecoration) styles['text-decoration'] = titleStyleObj.textDecoration;
      if (titleStyleObj.textAlign) styles['text-align'] = titleStyleObj.textAlign;
      return styles;
    }

    // Fallback to section-level title
    if (this.pageTitleData?.title && typeof this.pageTitleData.title === 'object') {
      const title = this.pageTitleData.title;
      const titleStyleObj = title.style?.text_style;

      if (titleStyleObj && typeof titleStyleObj === 'object') {
        const styles: any = {};
        if (titleStyleObj.fontWeight) styles['font-weight'] = titleStyleObj.fontWeight;
        if (titleStyleObj.fontStyle) styles['font-style'] = titleStyleObj.fontStyle;
        if (titleStyleObj.textDecoration) styles['text-decoration'] = titleStyleObj.textDecoration;
        if (titleStyleObj.textAlign) styles['text-align'] = titleStyleObj.textAlign;
        return styles;
      }
    }

    return {};
  }

  /**
   * Get hero section element for selection
   */
  getHeroSectionElement(): WebsiteElement | undefined {
    return this.elements.find(el => (el as any).originalKey === 'hero_section');
  }

  /**
   * Get hero title text
   */
  getHeroTitle(): string {
    if (this.heroSectionData?.title) {
      if (typeof this.heroSectionData.title === 'string') {
        return this.heroSectionData.title;
      } else if (this.heroSectionData.title?.label) {
        return this.heroSectionData.title.label;
      }
    }
    return 'Reliable Fleet Washing for Your Business';
  }

  /**
   * Get hero subtitle text
   */
  getHeroSubtitle(): string {
    if (this.heroSectionData?.subtitle) {
      if (typeof this.heroSectionData.subtitle === 'string') {
        return this.heroSectionData.subtitle;
      } else if (this.heroSectionData.subtitle?.label) {
        return this.heroSectionData.subtitle.label;
      }
    }
    return 'Keep your vehicles looking sharp with fast, professional wash care.';
  }

  /**
   * Get hero title styles
   */
  getHeroTitleStyles(): any {
    if (this.heroSectionData?.title && typeof this.heroSectionData.title === 'object') {
      const title = this.heroSectionData.title;
      const titleStyleObj = title.style;

      if (titleStyleObj && typeof titleStyleObj === 'object') {
        const styles: any = {};
        if (titleStyleObj.fontWeight) styles['font-weight'] = titleStyleObj.fontWeight;
        if (titleStyleObj.fontStyle) styles['font-style'] = titleStyleObj.fontStyle;
        if (titleStyleObj.textDecoration) styles['text-decoration'] = titleStyleObj.textDecoration;
        if (titleStyleObj.textAlign) styles['text-align'] = titleStyleObj.textAlign;
        return styles;
      }
    }
    return {};
  }

  /**
   * Get hero subtitle styles
   */
  getHeroSubtitleStyles(): any {
    if (this.heroSectionData?.subtitle && typeof this.heroSectionData.subtitle === 'object') {
      const subtitle = this.heroSectionData.subtitle;
      const subtitleStyleObj = subtitle.style;

      if (subtitleStyleObj && typeof subtitleStyleObj === 'object') {
        const styles: any = {};
        if (subtitleStyleObj.text_color) styles['color'] = subtitleStyleObj.text_color;
        if (subtitleStyleObj.font_size) styles['font-size'] = subtitleStyleObj.font_size;
        if (subtitleStyleObj.font_weight) styles['font-weight'] = subtitleStyleObj.font_weight;
        if (subtitleStyleObj.margin_bottom)
          styles['margin-bottom'] = subtitleStyleObj.margin_bottom;
        return styles;
      }
    }
    return {};
  }

  /**
   * Get hero title color
   */
  getHeroTitleColor(): string {
    if (this.heroSectionData?.title?.style?.text_color) {
      return this.heroSectionData.title.style.text_color;
    }
    return this.headingColor || '#15172E';
  }

  /**
   * Get hero subtitle color
   */
  getHeroSubtitleColor(): string {
    if (this.heroSectionData?.subtitle?.style?.text_color) {
      return this.heroSectionData.subtitle.style.text_color;
    }
    return this.paragraphColor || '#FFFFFF';
  }

  /**
   * Get hero background
   */
  getHeroBackground(): string {
    if (this.heroSectionData?.layout?.background) {
      return this.heroSectionData.layout.background;
    }
    // Default gradient from API
    return 'linear-gradient(135deg, #A8D5E2 0%, #8BC4D4 100%)';
  }

  /**
   * Get hero image
   */
  getHeroImage(): string | null {
    if (this.heroSectionData?.image?.src) {
      return this.heroSectionData.image.src;
    }
    return null;
  }

  /**
   * Get hero image alt text
   */
  getHeroImageAlt(): string {
    if (this.heroSectionData?.image?.alt) {
      return this.heroSectionData.image.alt;
    }
    return 'Fleet Washing Service';
  }

  /**
   * Handle hero section click
   */
  onHeroSectionClick(event: Event): void {
    event.stopPropagation();
    const heroElement: WebsiteElement = {
      id: 'hero_section',
      type: 'hero',
      content: {
        ...(this.heroSectionData || {}),
        title: this.getHeroTitle(),
        subtitle: this.getHeroSubtitle(),
        image: this.getHeroImage(),
        backgroundColor: this.getHeroBackground(),
      },
      selected: true,
      originalKey: 'hero_section',
    } as any;
    this.logger.debug('🚛 Hero section clicked, emitting element:', heroElement);
    this.elementSelect.emit(heroElement);
  }

  /**
   * Get form theme
   */
  getFormTheme(): 'light' | 'dark' {
    return this.formSectionData?.theme || this.formSectionData?.formTheme || 'light';
  }

  /**
   * Get button text
   */
  getButtonText(): string {
    return this.formSectionData?.buttonText || this.formSectionData?.button?.text || 'Submit';
  }

  /**
   * Get fleet section element for selection
   */
  getFleetSectionElement(): WebsiteElement | undefined {
    return this.elements.find(
      el =>
        el.type === 'fleets' ||
        el.id === 'fleets' ||
        (el as any).originalKey === 'page-title' ||
        (el as any).originalKey === 'form_section'
    );
  }

  /**
   * Get page header section background color
   */
  getPageHeaderSectionBackgroundColor(): string | null {
    // Priority 1: Check element content (most up-to-date, updated immediately)
    const pageHeaderElement = this.getFleetSectionElement();
    const pageHeaderContent = pageHeaderElement?.content as any;
    if (
      pageHeaderContent?.backgroundColor &&
      (pageHeaderElement as any)?.originalKey === 'page-title'
    ) {
      return pageHeaderContent.backgroundColor;
    }
    // Priority 2: Check pageTitleData (from API/pageData)
    if (this.pageTitleData?.backgroundColor) {
      return this.pageTitleData.backgroundColor;
    }
    // Priority 3: Check form section background color (also saved there)
    if (this.formSectionData?.backgroundColor) {
      return this.formSectionData.backgroundColor;
    }
    // Priority 4: Check page-level theme background color
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    if (page?.theme?.background_color) {
      return page.theme.background_color;
    }
    // Priority 5: Check pageData directly
    const sections = page?.sections || {};
    const pageTitleSection = sections['page-title'] || {};
    if (pageTitleSection.backgroundColor) {
      return pageTitleSection.backgroundColor;
    }
    // Priority 6: Check form section in pageData
    const formSection = sections.form_section || {};
    if (formSection.backgroundColor) {
      return formSection.backgroundColor;
    }
    // Default fallback
    return null;
  }

  /**
   * Get form section background color (same as page header section)
   */
  getFormSectionBackgroundColor(): string | null {
    // Use the same background color as the page header section
    return this.getPageHeaderSectionBackgroundColor();
  }

  /**
   * Handle hero section click
   */
  onPageHeaderSectionClick(event: Event): void {
    // Don't handle section click if clicking on navigation items, CTA button, or hamburger menu
    const target = event.target as HTMLElement;
    if (
      target.closest('.nav-link') ||
      target.closest('.book-wash-btn') ||
      target.closest('.hamburger-menu') ||
      target.closest('.mobile-menu-overlay') ||
      target.closest('.mobile-menu-drawer') ||
      target.closest('.page-header-header-wrapper')
    ) {
      return; // Let navigation handle the click
    }

    event.stopPropagation();
    const fleetElement: WebsiteElement = {
      id: 'fleets',
      type: 'fleets',
      content: {
        ...(this.pageTitleData || {}),
        title: this.getPageTitle(),
        subtitle: this.getPageSubtitle(),
        formTheme: this.getFormTheme(),
        buttonText: this.getButtonText(),
        backgroundColor: this.formSectionData?.backgroundColor || '#FFFFFF',
      },
      selected: true,
      originalKey: 'page-title',
    } as any;
    this.logger.debug('🚛 Hero section clicked, emitting element:', fleetElement);
    this.elementSelect.emit(fleetElement);
  }

  /**
   * Handle form section click
   */
  onFormSectionClick(event: Event): void {
    const target = event.target as HTMLElement;
    // Don't handle section click if clicking on dropdowns or form inputs
    if (
      target.closest('.dropdown-wrapper') ||
      target.closest('.dropdown-input') ||
      target.closest('.dropdown-menu') ||
      target.closest('input') ||
      target.closest('select') ||
      target.closest('textarea') ||
      target.closest('button')
    ) {
      return; // Let dropdown/form elements handle the click
    }

    event.stopPropagation();
    const fleetElement: WebsiteElement = {
      id: 'fleets',
      type: 'fleets',
      content: {
        ...(this.pageTitleData || {}),
        title: this.getPageTitle(),
        subtitle: this.getPageSubtitle(),
        formTheme: this.getFormTheme(),
        buttonText: this.getButtonText(),
        backgroundColor: this.formSectionData?.backgroundColor || '#FFFFFF',
      },
      selected: true,
      originalKey: 'form_section',
    } as any;
    this.logger.debug('🚛 Form section clicked, emitting element:', fleetElement);
    this.elementSelect.emit(fleetElement);
  }

  /**
   * Get footer quick links, preferring configured footerContent.quickLinks and falling back to visible pages.
   * @example
   * etFooterQuickLinks()
   * [{ label: 'Home', link: '#home' }, { label: 'About', link: '#about' }]
   * @returns {{Array<{ label: string; link: string }>}} Array of quick link objects with label and link.
   **/
  getFooterQuickLinks(): { label: string; link: string }[] {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const storedLinks = Array.isArray(footerContent?.quickLinks) ? footerContent.quickLinks : [];
    return resolveFooterQuickLinks(storedLinks, this.pages);
  }

  /**
   * Extracts social link objects (platform, url, icon) from the component's footer content, handling multiple possible footer data structures.
   * @example
   * etFooterSocialLinks()
   * [
   *   { platform: "facebook", url: "https://facebook.com/example", icon: "facebook" },
   *   { platform: "twitter", url: "https://twitter.com/example", icon: "twitter" }
   * ]
   * @returns {Array<{ platform: string; url: string; icon: string }>} Array of social link objects with platform, url and icon as strings.
   **/
  getFooterSocialLinks(): { platform: string; url: string; icon: string }[] {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;

    // Handle different data structures:
    // 1. socialLinks as array (from website builder)
    if (footerContent?.socialLinks && Array.isArray(footerContent.socialLinks)) {
      return footerContent.socialLinks.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }

    // 2. socialLinks as object with platforms array (from website preview)
    if (
      footerContent?.socialLinks?.platforms &&
      Array.isArray(footerContent.socialLinks.platforms)
    ) {
      return footerContent.socialLinks.platforms.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }

    // 3. Check for social_links.platforms (alternative structure)
    if (
      footerContent?.social_links?.platforms &&
      Array.isArray(footerContent.social_links.platforms)
    ) {
      return footerContent.social_links.platforms.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }

    // 4. Check for social_links as array
    if (footerContent?.social_links && Array.isArray(footerContent.social_links)) {
      return footerContent.social_links.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }

    return [];
  }

  getFooterSocialLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const title = footerContent?.socialLinksTitle;
    if (typeof title === 'string') {
      return title;
    } else if (title && typeof title === 'object' && title.text) {
      return title.text;
    }
    return 'Connect with us';
  }

  getFooterQuickLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const title = footerContent?.quickLinksTitle;
    if (typeof title === 'string') {
      return title;
    } else if (title && typeof title === 'object' && title.text) {
      return title.text;
    }
    return 'Quick links';
  }

  getFooterCopyright(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const copyright = footerContent?.copyright;
    if (typeof copyright === 'string') {
      return copyright;
    } else if (copyright && typeof copyright === 'object' && copyright.text) {
      return copyright.text;
    }
    return `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`;
  }

  /**
   * Retrieve the footer logo (prefers footer-defined logo, falls back to header logo or a default).
   * @example
   * etFooterLogo()
   * { text: 'way', image: 'logo.png' }
   * @param {void} none - No arguments.
   * @returns {{ text?: string; image?: string } | undefined} Returns an object with optional text and image for the logo, or undefined if no logo information is present.
   **/
  getFooterLogo(): { text?: string; image?: string } | undefined {
    // Get logo from footer element content
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;

    if (footerContent?.logo) {
      const logo = footerContent.logo;
      return {
        text: logo.text || 'way',
        image: logo.image || logo.url || '',
      };
    }

    // Fallback to header logo if footer logo not available
    const headerLogo = this.getHeaderLogo();
    if (headerLogo && headerLogo.image) {
      return {
        text: headerLogo.text,
        image: headerLogo.image,
      };
    }
    return { text: 'way' };
  }

  getFooterBackgroundColor(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.backgroundColor) {
      return footerContent.backgroundColor;
    }
    return '#000920';
  }

  getHeaderElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'header');
  }

  getFooterElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'footer');
  }

  /**
   * Handle clicks on the header area; if the click target is a navigation link, CTA button, hamburger menu, or mobile menu element, stop propagation, otherwise retrieve the header element and delegate to onElementClick.
   * @example
   * nHeaderClick(new Event('click'))
   * undefined
   * @param {{Event}} {{event}} - The click Event to handle.
   * @returns {{void}} No return value; performs click handling and may stop propagation.
   **/
  onHeaderClick(event: Event): void {
    // Don't propagate if clicking on navigation items, CTA button, or hamburger menu
    const target = event.target as HTMLElement;
    if (
      target.closest('.nav-link') ||
      target.closest('.book-wash-btn') ||
      target.closest('.hamburger-menu') ||
      target.closest('.mobile-menu-overlay') ||
      target.closest('.mobile-menu-drawer')
    ) {
      event.stopPropagation();
      return;
    }

    const headerElement = this.getHeaderElement();
    if (headerElement) {
      this.onElementClick(headerElement);
    }
  }

  onElementClick(element: WebsiteElement): void {
    if (!element) return;
    this.elementSelect.emit(element);
  }

  /**
   * Handle a click on the footer: stop propagation, ensure the footer element has type and id 'footer', and forward the element to onElementClick.
   * @example
   * nFooterClick(new MouseEvent('click'))
   * undefined
   * @param {Event} event - The DOM event triggered by clicking the footer.
   * @returns {void} Does not return a value; may call onElementClick if a footer element is present.
   **/
  onFooterClick(event: Event): void {
    event.stopPropagation();
    const footerElement = this.getFooterElement();
    if (footerElement) {
      // Ensure footer element has correct type and id
      if (!footerElement.type || footerElement.type !== 'footer') {
        footerElement.type = 'footer';
      }
      if (!footerElement.id || footerElement.id !== 'footer') {
        footerElement.id = 'footer';
      }
      this.onElementClick(footerElement);
    }
  }

  onNavigationClick(pageType: string): void {
    this.navigationClick.emit(pageType);
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
